import Slider from "components/DesignSystem/Slider/Slider";
import { AmountSuperScript } from "components/design/AmountSuperScript";
import { NexusTrackerTransaction, StateData } from "types/Models/nexusTracker";
import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from "react-table-8.10.7";
import { ReactNode, useMemo, useState } from "react";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useGetNexusTrackerTransactionsQuery } from "store/apis/salesTax";
import { formatDate } from "utils/formatDate";
import { PaginationHandler } from "components/DesignSystem/Pagination/Pagination";
import ToolTip from "components/design/toolTip";
import { Avatar } from "components/DesignSystem/AvatarGroup/Avatar";
import { UserGroup } from "components/icons/UserGroup";
import Loader from "components/DesignSystem/Loader/loader";
import { TableUI } from "components/design/TableUI";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import * as Popover from "@radix-ui/react-popover";
import { Button } from "components/DesignSystem/Button/Button";
import ThreeDots from "static/images/ThreeDots.svg";
import Modal from "components/DesignSystem/Modal/Modal";
import { useModal } from "hooks/useModal";
import { EditTransactions } from "./EditTransactions";
import { useDeleteEntityInvoiceMutation } from "store/apis/invoices";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { getBillingCartUrl } from "util/getBillingCartUrl";
import { useToast } from "hooks/useToast";
import { BackendError } from "types/utils/error";
import { SALES_TAX_INVOICE } from "constants/invoiceType";
import { NexusTrackerStatus } from "./NexusTrackerStatus";
import {
  ActionButtonText,
  getActionButtonText,
  getDisplayStatus,
} from "./ActionCell";
import { useGetCountriesDataQuery } from "store/apis/onboarding";
import { MONITORING } from "constants/nexusTrackerStatus";
import { FILING_INFO_MISSING } from "constants/salesTaxRegistrationStatusType";
import { useHistory, useRouteMatch } from "react-router-dom";
import { useLazyGetFilingDetailsByStateIdQuery } from "store/apis/filing";
import { OfficeIcon } from "components/icons/Office";
import { CircleRightIcon } from "components/icons/CircleRight";
import { WarehourseIcon } from "components/icons/Warehouse";
import { EmployeesIcon } from "components/icons/Employees";
import { CircleWrongIcon } from "components/icons/CircleWrong";
import { EmptySalesTax } from "components/Illustrations/EmptySalesTax";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { EditPencil } from "components/icons/EditPencil";
import { RegisterStateDropdown } from "components/SalesTax/RegisterStateDropdown";

const PhysicalNexusItem = ({
  icon,
  title,
  has = false,
}: {
  icon: ReactNode;
  title: string;
  has?: boolean;
}) => {
  return (
    <div className="t-flex t-items-center t-gap-x-1">
      {icon}
      <span className="t-text-body-sm t-text-text-60">{title}:</span>
      {has ? <CircleRightIcon /> : <CircleWrongIcon />}
    </div>
  );
};
const PhysicalNexus = ({
  activeStateDetails,
}: {
  activeStateDetails: StateData;
}) => {
  return (
    <div className="t-flex t-items-center t-gap-x-10 sm:t-flex-wrap sm:t-gap-x-4 sm:t-gap-y-2">
      <PhysicalNexusItem
        title="Office"
        icon={<OfficeIcon />}
        has={Boolean(activeStateDetails.office_present)}
      />
      <PhysicalNexusItem
        title="Warehouse"
        icon={<WarehourseIcon />}
        has={Boolean(activeStateDetails.warehouse_present)}
      />
      <PhysicalNexusItem
        title="Employee"
        icon={<EmployeesIcon />}
        has={Boolean(activeStateDetails.employees_present)}
      />
    </div>
  );
};

const transactionColumnHelper = createColumnHelper<NexusTrackerTransaction>();

const TransactionDeleteConfirmationModal = ({
  open,
  onClose,
  onConfirm,
  isLoading,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isLoading?: boolean;
}) => {
  return (
    <Modal.Root open={open} onOpenChange={onClose}>
      <Modal.Portal>
        <Modal.Overlay />
        <Modal.Content className="!t-p-0 md:!t-w-[440px]">
          <Modal.Header>
            <Modal.Title>Delete transaction</Modal.Title>
            <Modal.Close />
          </Modal.Header>

          <Modal.Body>
            <p className="t-text-body-sm t-m-0 t-text-text-60">
              This invoice will be permanently deleted and cannot be recovered.
              Are you sure you want to proceed?
            </p>
          </Modal.Body>
          <Modal.FooterButtonGroup>
            <Button onClick={onClose}>Cancel</Button>
            <Button
              customType="danger"
              isLoading={isLoading}
              onClick={onConfirm}
            >
              Delete
            </Button>
          </Modal.FooterButtonGroup>
        </Modal.Content>
      </Modal.Portal>
    </Modal.Root>
  );
};

const NexusTransactions = ({
  jurisdiction,
  year,
}: {
  jurisdiction: string;
  year: number;
}) => {
  const [pageNum, setPageNum] = useState(1);
  const entityId = useCurrentEntityId();
  const [selectedTransactionId, setSelectedTransactionId] = useState<
    string | null
  >(null);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string | null>(
    null
  );

  const {
    data: transactionsData,
    isLoading,
    isSuccess,
  } = useGetNexusTrackerTransactionsQuery({
    entityId,
    jurisdictionName: jurisdiction,
    pageNum,
    year,
  });

  const [deleteTransaction, { isLoading: isTransactionDeleting }] =
    useDeleteEntityInvoiceMutation();

  const transactionDeleteConfirmation = useModal();
  const editTransactions = useModal();

  const handleEditTransactions = (invoiceId: string, transactionId: string) => {
    editTransactions.open();
    setSelectedTransactionId(transactionId);
    setSelectedInvoiceId(invoiceId);
  };

  const columns = useMemo(
    () => [
      transactionColumnHelper.accessor("date", {
        header: "Date",
        cell: (info) => formatDate(info.getValue()),
        size: 30,
      }),
      transactionColumnHelper.accessor("customer_name", {
        header: "Customer",
        cell: (info) => (
          <div>
            <div className="t-text-body-sm t-text-text-60">
              {info.getValue()}
            </div>
            <div className="t-text-body-sm t-text-text-30">
              {info.row.original.customer_email}
            </div>
          </div>
        ),
        size: 30,
      }),
      transactionColumnHelper.accessor("amount", {
        header: () => <div className="t-text-right">Amount</div>,
        cell: (info) => (
          <div className="t-text-right">
            <AmountSuperScript amount={info.getValue()} />
          </div>
        ),
        size: 25,
      }),
      transactionColumnHelper.accessor("source", {
        header: "Source",
        cell: (info) => {
          const { logo_url, customer_name } = info.row.original;
          return (
            <div className="t-flex t-justify-center">
              {logo_url ? (
                <ToolTip text={customer_name}>
                  <span>
                    <Avatar size="small" src={logo_url} alt={info.getValue()} />
                  </span>
                </ToolTip>
              ) : (
                <ToolTip text="Manual">
                  <span>
                    <UserGroup />
                  </span>
                </ToolTip>
              )}
            </div>
          );
        },
        size: 15,
      }),
      transactionColumnHelper.accessor("uuid", {
        header: "",
        cell: (info) => {
          return (
            <Popover.Root>
              <Dropdown.Root>
                <Dropdown.Trigger asChild>
                  <Popover.Anchor asChild>
                    <div>
                      <Button size="xsmall" customType="ghost_icon">
                        <img
                          src={ThreeDots}
                          alt="Action"
                          className="t-select-none"
                        />
                      </Button>
                    </div>
                  </Popover.Anchor>
                </Dropdown.Trigger>

                <Dropdown.Portal>
                  <Dropdown.Content
                    sideOffset={8}
                    side="bottom"
                    className="t-mr-16"
                    align="start"
                  >
                    <Dropdown.Item
                      onSelect={() => {
                        handleEditTransactions(
                          info.row.original.invoice_id,
                          info.row.original.uuid
                        );
                      }}
                    >
                      Edit
                    </Dropdown.Item>
                    <Dropdown.Item
                      type="danger"
                      onSelect={() => {
                        setSelectedTransactionId(info.row.original.invoice_id);
                        transactionDeleteConfirmation.open();
                      }}
                    >
                      Delete
                    </Dropdown.Item>
                  </Dropdown.Content>
                </Dropdown.Portal>
              </Dropdown.Root>
            </Popover.Root>
          );
        },
      }),
    ],
    []
  );
  const group = useCurrentGroupContext();
  const { alertToast, successToast } = useToast();

  const table = useReactTable({
    data: transactionsData?.transactions || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const handleTransactionDelete = async () => {
    try {
      await deleteTransaction({
        groupId: group.uuid,
        entityId,
        invoiceId: selectedTransactionId!,
        type: SALES_TAX_INVOICE,
      }).unwrap();
      successToast({
        message: "Transaction deleted successfully",
      });
      transactionDeleteConfirmation.close();
    } catch (error) {
      alertToast(
        { message: (error as BackendError).data?.error?.message },
        error as {}
      );
    }
  };

  if (isLoading) {
    return (
      <div className="t-flex t-items-center t-justify-center t-h-full">
        <Loader size="small" />
      </div>
    );
  }

  return (
    <div>
      <div className="t-flex t-justify-between t-items-center t-mb-4">
        <p className="t-text-subtitle-sm t-text-text-60 t-m-0">Transactions</p>
        <PaginationHandler
          page_num={pageNum}
          per_page={transactionsData?.per_page ?? 0}
          total_count={transactionsData?.total_count ?? 0}
          total_pages={transactionsData?.total_pages ?? 0}
          onPageNumChange={setPageNum}
        />
      </div>
      <Async.Root
        isSuccess={Boolean(isSuccess)}
        isEmpty={Boolean(!isLoading && !transactionsData?.transactions?.length)}
        isLoading={false}
      >
        <Async.Success>
          <TableUI table={table} size="regular" />
        </Async.Success>
        <Async.Empty>
          <div className="t-flex t-flex-col t-items-center t-w-full t-pt-20">
            <EmptySalesTax />
            <p className="t-m-0 t-text-text-30 t-text-body-sm t-mt-6">
              No transactions added yet
            </p>
          </div>
        </Async.Empty>
      </Async.Root>

      <TransactionDeleteConfirmationModal
        open={transactionDeleteConfirmation.isOpen}
        onClose={transactionDeleteConfirmation.close}
        onConfirm={handleTransactionDelete}
        isLoading={isTransactionDeleting}
      />
      <EditTransactions
        isOpen={editTransactions.isOpen}
        onClose={editTransactions.close}
        selectedInvoiceId={selectedInvoiceId}
        selectedTransactionId={selectedTransactionId}
      />
    </div>
  );
};

export const NexusTrackerSlider = ({
  activeStateDetails,
  activeState,
  setActiveState,
  onEditPhysicalPresence,
}: {
  activeStateDetails: StateData;
  activeState: string | null;
  setActiveState: (value: string | null) => void;
  onEditPhysicalPresence: (stateId: string) => void;
}) => {
  const status = getDisplayStatus(activeStateDetails);
  const isInCart = activeStateDetails.cart_status === "IN_CART";
  const hasLaunchedTask = Boolean(activeStateDetails.launched_task_id);

  const footerButtonText = getActionButtonText(
    status,
    isInCart,
    hasLaunchedTask
  );
  const { data: countries } = useGetCountriesDataQuery();
  const { location, push } = useHistory();
  const { path } = useRouteMatch();
  const { uuid: groupId } = useCurrentGroupContext();
  const { isAdmin } = useRoleBasedView();

  const [getFilingDetailsByStateId] = useLazyGetFilingDetailsByStateIdQuery();
  const entityId = useCurrentEntityId();

  const unitatedStates = countries?.find(
    (country) => country.name === "United States"
  )?.states;

  const activeStateId = unitatedStates?.find(
    (state) => state.name === activeStateDetails.jurisdiction
  )?.uuid;

  const handleEditPhysicalPresence = () => {
    if (!activeStateId) {
      return;
    }
    onEditPhysicalPresence(activeStateId);
  };

  const handleClick = async (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    e.stopPropagation();

    if (footerButtonText === ActionButtonText.GO_TO_CART) {
      const cartUrl = getBillingCartUrl({ isAdmin, groupId, entityId });
      window.open(cartUrl, "_blank");
      return;
    }

    if (
      footerButtonText === ActionButtonText.REMITTING &&
      activeStateDetails.launched_task_id
    ) {
      push(`/tax/filings/${activeStateDetails.launched_task_id}`);
      return;
    }

    const stateId = activeStateId;

    if (!stateId) {
      return;
    }

    if (footerButtonText === ActionButtonText.REMITTING) {
      const currentParams = new URLSearchParams(location.search);
      try {
        const resp = await getFilingDetailsByStateId({
          stateId,
          entityId,
        }).unwrap();
        currentParams.set("tax_year", String(resp.filing_tax_year));
        push(`${path}/state-filings?${currentParams.toString()}`);
      } catch (error) {
        // No filing exists yet for this state — the slider is already open, so nothing more to do.
      }
    }

    if (
      footerButtonText === ActionButtonText.REGISTERING ||
      footerButtonText === ActionButtonText.FILING_INFO_MISSING
    ) {
      const currentParams = new URLSearchParams(location.search);
      currentParams.set("type", "existing");
      currentParams.set("state_id", stateId);
      push(`${path}/registrations?${currentParams.toString()}`);
    }
  };

  const hasNoThresholds = !Boolean(
    activeStateDetails.transaction_count_limit ||
      activeStateDetails.transaction_amount_limit
  );
  const hasActiveRegistration =
    Boolean(activeStateDetails.state_registration_status) ||
    activeStateDetails.registration_status === FILING_INFO_MISSING;
  const isExempted = hasNoThresholds && !hasActiveRegistration;

  return (
    <Slider.Root
      open={Boolean(activeState)}
      onOpenChange={() => setActiveState(null)}
    >
      <Slider.Content width={600}>
        <Slider.Header>
          <Slider.Title>
            <div className="t-flex t-gap-2 t-items-center">
              <span>{activeState}</span>
              <NexusTrackerStatus status={status} isExempted={isExempted} />
            </div>
          </Slider.Title>
          <Slider.Close />
        </Slider.Header>
        <Slider.Body className="t-min-h-full">
          <div className="t-flex t-flex-col t-gap-6">
            <div className="t-flex t-flex-col t-gap-none t-border-solid t-border t-border-neutral-10 t-rounded-lg">
              {activeStateDetails.nexus_type.includes("Economic") &&
                !isExempted && (
                  <div className="t-pl-4 t-pt-4">
                    <span className="t-text-text-30 t-text-subtext-sm ">
                      Economic Nexus
                    </span>
                  </div>
                )}
              <div className="t-grid t-grid-cols-2 t-gap-x-4 t-gap-y-4 t-p-4">
                <div className="t-flex t-flex-col t-gap-2">
                  <p className="t-text-body-sm t-text-text-60 t-m-0">Sales</p>
                  <div className="t-flex t-gap-1.5 t-items-center">
                    <span className="t-text-body-sm t-text-text-30 t-m-0">
                      <AmountSuperScript
                        amount={activeStateDetails?.transactions_amount}
                      />{" "}
                      <span className="t-text-body-sm t-text-neutral-40 t-m-0">
                        /
                      </span>
                    </span>{" "}
                    <p className="t-text-body-sm t-text-text-30 t-m-0">
                      {activeStateDetails?.transaction_amount_limit ? (
                        <AmountSuperScript
                          amount={activeStateDetails?.transaction_amount_limit}
                        />
                      ) : (
                        "No limit"
                      )}
                    </p>
                  </div>
                </div>

                <div className="t-flex t-flex-col t-gap-2">
                  <p className="t-text-body-sm t-text-text-60 t-m-0">
                    Transactions
                  </p>
                  <div className="t-flex t-gap-1.5 t-items-center">
                    <span className="t-text-body-sm t-text-text-30 t-m-0">
                      {activeStateDetails?.transaction_count}{" "}
                      <span className="t-text-body-sm t-text-neutral-40 t-m-0">
                        /
                      </span>
                    </span>{" "}
                    <p className="t-text-body-sm t-text-text-30 t-m-0">
                      {activeStateDetails?.transaction_count_limit ||
                        "No limit"}
                    </p>
                  </div>
                </div>

                <div className="t-flex t-flex-col t-gap-2">
                  <p className="t-text-body-sm t-text-text-60 t-m-0">
                    Tax collected
                  </p>
                  <span className="t-text-body-sm t-text-text-30 t-m-0">
                    <AmountSuperScript
                      amount={Number(
                        activeStateDetails.total_tax_collected || 0
                      )}
                    />
                  </span>
                </div>
              </div>

              <div className="t-border-0 t-border-t t-border-dashed t-border-neutral-10 t-mx-4" />

              <div className="t-flex t-flex-col t-gap-2 t-p-4">
                <div className="t-flex t-items-center t-gap-1.5">
                  <span className="t-text-text-60 t-text-body-sm">
                    Physical presence
                  </span>
                  <Button
                    customType="ghost_icon"
                    size="xsmall"
                    onClick={handleEditPhysicalPresence}
                  >
                    <EditPencil />
                  </Button>
                </div>
                <PhysicalNexus activeStateDetails={activeStateDetails} />
              </div>
            </div>
            {activeState && (
              <div className="t-flex t-flex-col t-gap-none t-border-solid t-border t-border-neutral-10 t-rounded-lg t-p-4">
                <NexusTransactions
                  jurisdiction={activeState}
                  year={activeStateDetails.year}
                />
              </div>
            )}
          </div>
        </Slider.Body>
        {Boolean(status !== MONITORING && !isExempted) && (
          <Slider.Footer className="t-sticky t-bottom-0">
            {footerButtonText === ActionButtonText.EXPOSED && activeStateId ? (
              <RegisterStateDropdown
                entityId={entityId}
                stateId={activeStateId}
                stateName={activeStateDetails.jurisdiction}
              />
            ) : (
              <Button
                customType="primary"
                onClick={handleClick}
                size="small"
                block
              >
                {footerButtonText}
              </Button>
            )}
          </Slider.Footer>
        )}
      </Slider.Content>
    </Slider.Root>
  );
};
